import { AIProvider, ChatMessage } from '../services/chat/chat.service';

/**
 * Represents a saved chat conversation from the DOTS Copilot
 */
export interface ImportedFromMetadata {
  username: string;
  displayName: string;
  instanceUrl?: string;
  importedAt: string;
}

export interface IPromptAuditEntry {
  messageIndex?: number;
  provider: AIProvider;
  deliveryMode: 'direct' | 'chatkit';
  resolutionSource: 'global' | 'provider_override';
  appliedPrompt: string;
  label?: string;
  versionTag?: string;
  appliedAt: string;
  stateVariableName?: string;
}

export type ChatConversationScopeKind = 'generic' | 'replicate' | 'experiment';

export type ChatConversationSharingMode = 'private' | 'replicate_access';

export interface IConversationReferenceAnnotation {
  thingieId: string;
  processIdx: number;
  annotationIdx: number;
}

export interface IChatConversationScope {
  scopeKind: ChatConversationScopeKind;
  linkedThingieIds?: string[];
  experimentGroupId?: string;
  contextLabel?: string;
  sharingMode: ChatConversationSharingMode;
}

export interface IChatConversation {

  /**
   * Unique conversation ID
   */
  id: string;

  /**
   * Title of the conversation (auto-generated from first user message)
   */
  title: string;

  /**
   * Array of chat messages in the conversation
   */
  messages: ChatMessage[];

  /**
   * ISO timestamp when the conversation was created
   */
  createdAt: string;

  /**
   * ISO timestamp when the conversation was last updated
   */
  updatedAt: string;

  /**
   * User's overall rating for this conversation (1-5 stars)
   */
  rating?: number;

  /**
   * User's free-text feedback for this conversation
   */
  feedback?: string;

  /**
   * AI provider used for this conversation
   */
  provider?: AIProvider;

  /**
   * Specific model used (e.g. "gpt-5.2", "claude-sonnet-4-20250514")
   */
  modelUsed?: string;

  /**
   * Origin of the conversation: "direct" (Responses API) or "chatkit" (Agent Builder workflow).
   * Legacy conversations without this field are treated as "direct".
   */
  source?: 'direct' | 'chatkit';

  /**
   * OpenAI ChatKit thread ID (only set for chatkit-sourced conversations)
   */
  chatkitThreadId?: string;

  /**
   * Metadata about the original user when this conversation was imported from another instance
   */
  importedFrom?: ImportedFromMetadata;

  /**
   * Audit snapshots for hidden prompts applied during the conversation.
   * These are stored for admin review and are not rendered in the regular Copilot chat UI.
   */
  promptAuditTrail?: IPromptAuditEntry[];

  /**
   * Scope of the conversation.
   */
  scopeKind?: ChatConversationScopeKind;

  /**
   * Replicates linked to the conversation.
   */
  linkedThingieIds?: string[];

  /**
   * Stable experiment group identifier for experiment-scoped conversations.
   */
  experimentGroupId?: string;

  /**
   * Human-readable label shown in the Copilot UI.
   */
  contextLabel?: string;

  /**
   * Stable owner of the conversation.
   */
  ownerUserId?: string;

  /**
   * Access mode for the conversation.
   */
  sharingMode?: ChatConversationSharingMode;

  /**
   * Linked sidebar annotations created for scoped conversations.
   */
  referenceAnnotations?: IConversationReferenceAnnotation[];
}

export interface CopilotExport {
  formatVersion: 1;
  exportedAt: string;
  exportedBy: {
    username: string;
    displayName: string;
    instanceUrl?: string;
  };
  conversations: IChatConversation[];
}

/**
 * Generates a title from the first user message
 *
 * @param firstMessage The first message text from the user
 * @returns A truncated title (max 50 chars) at word boundary
 */
export function generateConversationTitle(firstMessage: string): string {
  const maxLength = 50;
  const cleaned = firstMessage.trim();
  if (cleaned.length <= maxLength) {
    return cleaned;
  }
  // Truncate at word boundary
  const truncated = cleaned.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  return (lastSpace > 30 ? truncated.substring(0, lastSpace) : truncated) + '...';
}
